import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Checkbox,
  Divider,
  HStack,
  Heading,
  Text,
  Tooltip,
  VStack,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { Card, CardFooter, CardHeader, CardBody } from '@chakra-ui/card';
import { isMobile } from 'react-device-detect';
import { FaReceipt, FaCalendarAlt, FaCreditCard } from 'react-icons/fa';
import { MemberProductStatus } from '../models/periodic-product-payment';
import CurrencyData from './currency-data';
import {
  badgeColor,
  getBadgeLabel,
  getDate,
  isBlockedByPreviousUnpaidMonths,
  paymentCheckboxIsDisabled,
  PREVIOUS_MONTHS_PAYMENT_REQUIRED_MESSAGE
} from '../helpers';

export type ProductFeeCardVariant = 'membership' | 'activity';

interface Props {
  productFee: MemberProductStatus;
  cardTitle: string;
  toPayList: MemberProductStatus[];
  allProducts: MemberProductStatus[];
  onChange: (fee: MemberProductStatus) => void;
  variant?: ProductFeeCardVariant;
}

const isInToPayList = (fee: MemberProductStatus, list: MemberProductStatus[]): boolean =>
  list.some(
    (payment) =>
      payment.product.id === fee.product.id || payment.productId === fee.product.id
  );

function ProductFeeCard(props: Props): JSX.Element {
  const variant = props.variant ?? 'activity';
  const [isChecked, setIsChecked] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorderWidth = useColorModeValue('0', '1px');
  const cardBorderColor = useColorModeValue('transparent', 'green.500');
  const hoverCardBorderColor = useColorModeValue('transparent', 'green.400');
  const perforatedBorder = useColorModeValue('gray.300', 'gray.500');
  const headerBg = useColorModeValue('green.50', 'green.900');
  const headerBorderColor = useColorModeValue('green.200', 'green.500');
  const footerBg = useColorModeValue('gray.50', 'gray.700');
  const footerBorderColor = useColorModeValue('gray.300', 'gray.500');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');
  const priceColor = useColorModeValue('green.600', 'green.300');
  const checkboxBg = useColorModeValue('orange.50', 'orange.900');
  const checkboxBorderColor = useColorModeValue('orange.200', 'orange.600');
  const checkboxTextColor = useColorModeValue('orange.700', 'orange.200');
  const perforation1 = useColorModeValue('transparent', 'gray.800');
  const perforation2 = useColorModeValue('white', 'gray.600');
  const priceBg = useColorModeValue('green.50', 'green.900');
  const priceBorderColor = useColorModeValue('green.200', 'green.500');

  const perforationGradient = `repeating-linear-gradient(90deg, ${perforation1} 0px, ${perforation1} 8px, ${perforation2} 8px, ${perforation2} 16px)`;
  const completedBadgeLabel = variant === 'membership' ? 'Abonada' : 'Abonado';

  useEffect(() => {
    setIsChecked(isInToPayList(props.productFee, props.toPayList));
  }, [props.productFee, props.toPayList]);

  const handleChange = (): void => {
    props.onChange(props.productFee);
  };

  const displayValue =
    props.productFee.remainingPrice === 0
      ? props.productFee.paidAmount
      : props.productFee.remainingPrice;

  const amountValue = variant === 'membership' ? props.productFee.originalPrice : displayValue;
  const amountLabel =
    variant === 'membership' || !props.productFee.isCompleted ? 'Importe:' : 'Valor abonado:';

  const getProductTitle = (): string => {
    if (variant === 'membership') {
      return props.productFee.productName;
    }
    return props.productFee.product.shortName;
  };

  const getCompletedPaymentInfo = (): JSX.Element => {
    if (variant === 'membership') {
      const lastPayment = props.productFee.payments?.at(props.productFee.payments.length - 1);
      const date = lastPayment?.paymentDate
        ? new Date(lastPayment.paymentDate).toLocaleDateString('es-AR').split('T')[0]
        : null;

      return (
        <VStack alignItems="flex-start" spacing={2}>
          {date && (
            <HStack>
              <Icon as={FaCalendarAlt} color="green.500" />
              <Text fontSize="sm" color={secondaryTextColor}>
                Fecha de pago: {date}
              </Text>
            </HStack>
          )}
          <HStack>
            <Icon as={FaCreditCard} color="green.500" />
            <Text fontSize="sm" color={secondaryTextColor}>
              Valor abonado: <CurrencyData value={props.productFee.paidAmount} />
            </Text>
          </HStack>
        </VStack>
      );
    }

    return (
      <VStack alignItems="flex-start" spacing={2}>
        {props.productFee.payments?.map((pd) => (
          <HStack key={pd.paymentDetailId}>
            <Icon as={FaCalendarAlt} color="green.500" />
            <Text fontSize="sm" color={secondaryTextColor}>
              Fecha de pago: {getDate(pd.paymentDate, 'dd-mm-yyyy HH:MM:ss', false)}
            </Text>
          </HStack>
        ))}
        {props.productFee.paidAmount > 0 && (
          <HStack>
            <Icon as={FaCreditCard} color="green.500" />
            <Text fontSize="sm" color={secondaryTextColor}>
              Valor abonado: <CurrencyData value={props.productFee.paidAmount} />
            </Text>
          </HStack>
        )}
      </VStack>
    );
  };

  const getPayCheckbox = (productFee: MemberProductStatus) => {
    if (productFee.isCompleted) {
      return getCompletedPaymentInfo();
    }

    const isBlockedByPreviousMonths = isBlockedByPreviousUnpaidMonths(
      productFee,
      props.allProducts,
      props.toPayList
    );
    const checkbox = (
      <Checkbox
        width="100%"
        isChecked={isChecked}
        onChange={handleChange}
        disabled={paymentCheckboxIsDisabled(
          productFee,
          props.allProducts,
          props.toPayList,
          isChecked
        )}
        colorScheme="orange"
        size="lg"
      >
        <Text fontWeight="semibold" color={checkboxTextColor}>
          Seleccionar para abonar
        </Text>
      </Checkbox>
    );

    return (
      <Box
        bg={checkboxBg}
        p={3}
        borderRadius="lg"
        border={`2px solid ${checkboxBorderColor}`}
        w="full"
      >
        {isBlockedByPreviousMonths && !isChecked ? (
          <Tooltip label={PREVIOUS_MONTHS_PAYMENT_REQUIRED_MESSAGE} placement="top">
            <Box>{checkbox}</Box>
          </Tooltip>
        ) : (
          checkbox
        )}
      </Box>
    );
  };

  return (
    <Card
      width="100%"
      bg={cardBg}
      borderWidth={cardBorderWidth}
      borderStyle="solid"
      borderColor={cardBorderColor}
      boxShadow="xl"
      borderRadius="lg"
      overflow="hidden"
      position="relative"
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: '2xl',
        borderColor: hoverCardBorderColor
      }}
      transition="all 0.3s ease"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        height="8px"
        bg={perforationGradient}
        borderRadius="full"
      />

      <CardHeader
        bg={headerBg}
        borderBottom="1px solid"
        borderColor={headerBorderColor}
        py={3}
      >
        <HStack justify="space-between" align="center">
          <HStack>
            <Icon as={FaReceipt} color="green.500" />
            <Heading size={variant === 'membership' ? 'md' : 'sm'} color={textColor}>
              {props.cardTitle}
            </Heading>
          </HStack>
          <Badge
            colorScheme={badgeColor(props.productFee)}
            variant="solid"
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="full"
          >
            {getBadgeLabel(props.productFee, completedBadgeLabel)}
          </Badge>
        </HStack>
      </CardHeader>

      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              {getProductTitle()}
            </Text>
            <Divider my={2} borderStyle="solid" borderColor={perforatedBorder} />
          </Box>

          {variant === 'membership' && (
            <Text
              fontSize="sm"
              color={secondaryTextColor}
              minHeight="40px"
              lineHeight="1.4"
            >
              {isMobile
                ? props.productFee.product.shortName
                : props.productFee.product.description}
            </Text>
          )}

          <Box bg={priceBg} p={3} borderRadius="md" border={`1px solid ${priceBorderColor}`}>
            <HStack justify="space-between">
              <Text fontSize="sm" color={secondaryTextColor}>
                {amountLabel}
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color={priceColor}>
                <CurrencyData value={amountValue} />
              </Text>
            </HStack>
          </Box>
        </VStack>
      </CardBody>

      <CardFooter
        bg={footerBg}
        borderTop="1px solid"
        borderColor={footerBorderColor}
        p={4}
      >
        {getPayCheckbox(props.productFee)}
      </CardFooter>

      <Box
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        height="8px"
        bg={perforationGradient}
        borderRadius="full"
      />
    </Card>
  );
}

export default ProductFeeCard;
